import {
  type FormFieldDescriptor,
  type NarrowFieldsByValueType,
  type TidalFormBridgeOptions,
  type TidalFormState,
  useTidalFormBridge,
} from '@workwave-tidal/core/form'

import { CustomFieldOption } from '../../types'
import { CustomFieldInput } from './components/CustomFieldInput'

export interface CustomFieldListInputProps {
  testId?: string
  label: string
  options: CustomFieldOption[]
  maxSelectionsCount?: number
}

export type CustomFieldListValue = Record<string, string>
const defaultComponentValue: CustomFieldListValue = {}

type CustomFieldListRecord = Record<string, string>
type CustomFieldListRecordOnChange = (value: CustomFieldListRecord) => void

export type CustomFieldListProps<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = CustomFieldListValue,
> = {
  customFieldListInputProps: CustomFieldListInputProps
} & TidalFormBridgeOptions<CustomFieldListValue, TargetField, State, AcceptedFormValue>

export function CustomFieldList<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = CustomFieldListValue,
>(props: CustomFieldListProps<TargetField, State, AcceptedFormValue>) {
  const {
    customFieldListInputProps: { testId, label, options, maxSelectionsCount },
    initialValue = defaultComponentValue as FormFieldDescriptor<State>[TargetField]['value'],
    validateOn = 'change',
    ...bridgeOptions
  } = props

  const {
    fieldInfo: {
      value,
      onChange,
      fieldDisabled,
      errorOrHelperText,
      showErrorAccent,
      fieldHasError,
    },
  } = useTidalFormBridge<CustomFieldListValue, TargetField, State, AcceptedFormValue>({
    ...bridgeOptions,
    validateOn,
    initialValue,
  })

  return (
    <CustomFieldInput
      testId={testId}
      label={label}
      value={value as CustomFieldListRecord}
      disabled={fieldDisabled}
      onChange={onChange as CustomFieldListRecordOnChange}
      error={showErrorAccent && fieldHasError}
      helperText={errorOrHelperText}
      options={options}
      maxSelectionsCount={maxSelectionsCount}
    />
  )
}
